import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowDown } from "lucide-react";
import { API } from "@/App";
import { MaskedLine, FadeUp, Eyebrow } from "@/components/Reveal";
import { Vine, FloatLeaf, Leaf } from "@/components/Botanical";
import Marquee from "@/components/Marquee";
import PhotoMarquee from "@/components/PhotoMarquee";
import { SpotlightCoverLayer, SpotlightCoverGreeting } from "@/components/SpotlightCover";
import { BOOKING_URL } from "@/components/Nav";

const HERO_IMG = "/photos/salon-1.jpg";
const STORY_IMG = "/photos/salon-4.jpg";
const PRODUCT_IMGS = [
  "https://images.unsplash.com/photo-1762815716180-1d3a167828f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxvcmdhbmljJTIwaGFpciUyMHByb2R1Y3QlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4ODU0NjkwNXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1775126454577-4846f3e55cc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxvcmdhbmljJTIwaGFpciUyMHByb2R1Y3QlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4ODU0NjkwNXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1747858989102-cca0f4dc4a11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxvcmdhbmljJTIwaGFpciUyMHByb2R1Y3QlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4ODU0NjkwNXww&ixlib=rb-4.1.0&q=85",
];
const GALLERY = ["/photos/salon-2.jpg", "/photos/salon-3.jpg"];

const HOME_FALLBACK = {
  eyebrow: "A conscious salon",
  line1: "PURE.",
  line2: "NATURAL.",
  line3: "TIMELESS.",
  subtext:
    "Where your hair is cared for, not just styled. Organic products, non-toxic rituals, and unhurried one-to-one attention.",
};

const GALLERY_FALLBACK = {
  hero_image: HERO_IMG,
  story_image: STORY_IMG,
  gallery_images: GALLERY,
  videos: [
    { src: "/videos/salon-1.mp4", poster: "/videos/salon-1.jpg", label: "Inside the salon" },
    { src: "/videos/salon-2.mp4", poster: "/videos/salon-2.jpg", label: "The craft, in motion" },
    { src: "/videos/salon-3.mp4", poster: "/videos/salon-3.jpg", label: "Everyday rituals" },
    { src: "/videos/salon-4.mp4", poster: "/videos/salon-4.jpg", label: "The Orgænics touch" },
    { src: "/videos/salon-6.mp4", poster: "/videos/salon-6.jpg", label: "Details that matter" },
  ],
};

export function useContent(key, fallback) {
  const [data, setData] = useState(fallback);
  useEffect(() => {
    axios
      .get(`${API}/content/${key}`)
      .then((r) => setData({ ...fallback, ...r.data }))
      .catch(() => {});
  }, [key]);
  return data;
}

const CHAPTERS = [
  {
    n: "01",
    title: "More than how you look",
    body: "A great salon experience is about how you feel while you're here. We've created a warm, welcoming and professional space where quality, care and personal attention come together — every detail considered with the long-term health of your hair in mind.",
  },
  {
    n: "02",
    title: "Understanding you, first",
    body: "Great hair begins with understanding your style, your needs and what makes you feel your best. Every visit is designed around personalised, one-to-one care — never a rushed or impersonal experience.",
  },
  {
    n: "03",
    title: "A community, not a clientele",
    body: "Over the years we've built more than a clientele — a community of people who trust us with their hair, return for the experience, and become part of the Salon Orgænics family.",
  },
];

function SpinBadge() {
  return (
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
      className="absolute -left-12 bottom-16 w-28 h-28 md:w-36 md:h-36 pointer-events-none z-20"
      data-testid="hero-spin-badge"
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <path id="badge-circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
        </defs>
        <circle cx="50" cy="50" r="49" className="fill-[#11261A]" />
        <text className="fill-[#F9FAF8]" style={{ fontSize: 9.2, letterSpacing: "0.28em", textTransform: "uppercase" }}>
          <textPath href="#badge-circle">Pure · Natural · Timeless ·</textPath>
        </text>
        <circle cx="50" cy="50" r="10" fill="none" stroke="#8EA696" strokeWidth="0.8" />
        <path d="M50 44c2.4 3.2 2.4 8.8 0 12-2.4-3.2-2.4-8.8 0-12z" fill="#8EA696" />
      </svg>
    </motion.div>
  );
}


function Hero() {
  const home = useContent("home", HOME_FALLBACK);
  const gallery = useContent("gallery", GALLERY_FALLBACK);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const vineY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // cursor parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(mx, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(my, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative min-h-screen flex items-center overflow-hidden bg-[#F9FAF8]" data-testid="hero-section">
      <motion.div style={{ y: vineY }} className="absolute left-2 md:left-10 top-24 h-[70vh] opacity-40 pointer-events-none">
        <Vine className="h-full w-auto" />
      </motion.div>
      <FloatLeaf className="w-24 md:w-40 right-[8%] top-[14%] opacity-30" delay={0.4} />
      <FloatLeaf className="w-16 md:w-24 left-[38%] bottom-[10%] opacity-20" delay={1.2} color="#8EA696" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-32 pb-20 grid lg:grid-cols-12 gap-12 items-center relative z-10">
        <div className="lg:col-span-7">
          <MaskedLine onLoad delay={0.1} className="mb-8">
            <span className="text-[11px] uppercase tracking-[0.4em] text-[#1B3B28] font-medium flex items-center gap-3">
              <span className="w-10 h-px bg-[#1B3B28] inline-block" />
              {home.eyebrow}
            </span>
          </MaskedLine>

          <h1 className="font-display font-light leading-[0.92] tracking-tight text-[#0A0A0A]">
            <MaskedLine onLoad delay={0.25} className="text-[16vw] lg:text-[8.5rem]">
              <span className="text-[#11261A]">{home.line1}</span>
            </MaskedLine>
            <MaskedLine onLoad delay={0.4} className="text-[16vw] lg:text-[8.5rem]">
              <span className="italic text-[#11261A]">{home.line2}</span>
            </MaskedLine>
            <MaskedLine onLoad delay={0.55} className="text-[16vw] lg:text-[8.5rem]">
              <span className="text-outline">{home.line3}</span>
            </MaskedLine>
          </h1>

          <FadeUp delay={0.9} className="mt-10 max-w-md">
            <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed">
              {home.subtext}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-now-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#11261A] text-[#F9FAF8] text-[12px] uppercase tracking-[0.22em] px-8 py-4 transition-all duration-300 hover:bg-black hover:shadow-xl hover:-translate-y-0.5"
              >
                Book your visit
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/services"
                data-testid="hero-explore-menu-link"
                className="text-[12px] uppercase tracking-[0.22em] text-[#11261A] border-b border-[#11261A]/30 pb-1 hover:border-[#11261A] transition-colors duration-300"
              >
                Explore the menu
              </Link>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div style={{ y: imgY, rotateX: ry, rotateY: rx, transformPerspective: 1200 }} className="relative">
            {/* solid green arch echo, offset behind */}
            <motion.div
              initial={{ opacity: 0, x: 40, y: 40 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 1.4, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -inset-3 bg-[#11261A] rounded-t-[999px] translate-x-8 translate-y-8 pointer-events-none"
            />
            {/* hairline arch outline */}
            <div className="absolute -inset-4 border border-[#8EA696]/50 rounded-t-[999px] -translate-x-5 -translate-y-5 pointer-events-none" />
            {/* image arch with slow cinematic drift */}
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-t-[999px] overflow-hidden"
            >
              <motion.img
                src={gallery.hero_image || HERO_IMG}
                alt="Inside Salon Orgænics"
                data-testid="hero-image"
                animate={{ scale: [1, 1.07, 1], x: [0, -12, 0] }}
                transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
                className="w-full h-[72vh] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#11261A]/25 via-transparent to-transparent pointer-events-none" />
            </motion.div>
            <SpinBadge />
            <p className="absolute top-1/2 -right-9 -translate-y-1/2 text-[10px] uppercase tracking-[0.5em] text-[#1B3B28]/60 [writing-mode:vertical-rl]">
              Salon Orgænics · Kharadi
            </p>
            <FloatLeaf className="w-20 -left-10 top-20 opacity-50" delay={0.8} />
            <Leaf className="absolute -left-14 -bottom-10 w-28 opacity-50" />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 text-[#4A4A4A]"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={14} />
        </motion.span>
        <span className="text-[10px] uppercase tracking-[0.35em]">Open daily · 11 AM – 10 PM</span>
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative py-20 md:py-28 bg-[#F9FAF8] overflow-hidden" data-testid="manifesto-section">
      <Vine className="absolute right-4 md:right-16 top-10 h-[55%] w-auto opacity-30" flip />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <FadeUp>
          <Eyebrow>What we stand for — the manifesto</Eyebrow>
        </FadeUp>
        <div className="mt-12 space-y-16 md:space-y-20">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className={`grid md:grid-cols-12 gap-6 items-start ${i % 2 === 1 ? "md:text-left" : ""}`}>
              <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-2 md:col-start-10" : ""}`}>
                <MaskedLine>
                  <span className="font-display font-light text-7xl md:text-8xl text-[#11261A]/15 leading-none">{c.n}</span>
                </MaskedLine>
              </div>
              <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-4"}`}>
                <MaskedLine delay={0.1}>
                  <h3 className="font-display font-light text-4xl md:text-5xl text-[#0A0A0A] tracking-tight">{c.title}</h3>
                </MaskedLine>
                <FadeUp delay={0.25}>
                  <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] leading-relaxed max-w-xl">{c.body}</p>
                </FadeUp>
              </div>
            </div>
          ))}
        </div>
        <FadeUp className="mt-16">
          <Link
            to="/about"
            data-testid="manifesto-read-story-link"
            className="group inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.25em] text-[#11261A]"
          >
            Read our full story
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

const COVER_FALLBACK = { active: "none", greeting: "", custom_image: "" };

function Spotlight() {
  const [data, setData] = useState(null);
  const cover = useContent("spotlight_cover", COVER_FALLBACK);
  useEffect(() => {
    axios
      .get(`${API}/spotlight`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  const img = data ? PRODUCT_IMGS[data.index % PRODUCT_IMGS.length] : PRODUCT_IMGS[0];

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);

  return (
    <section ref={ref} className="relative bg-[#11261A] text-[#F9FAF8] grain py-20 md:py-28 overflow-hidden" data-testid="daily-spotlight-section">
      <SpotlightCoverLayer cover={cover} />
      <FloatLeaf className="w-32 left-[4%] top-[8%] opacity-20" color="#8EA696" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <SpotlightCoverGreeting cover={cover} />
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-14">
          <div>
            <FadeUp>
              <Eyebrow dark>The daily brand spotlight</Eyebrow>
            </FadeUp>
            <MaskedLine delay={0.1}>
              <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight mt-4">
                One brand. One product.<br />
                <span className="italic text-[#8EA696]">Chosen with intent.</span>
              </h2>
            </MaskedLine>
          </div>
          {data && (
            <FadeUp delay={0.2}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696]" data-testid="spotlight-date">
                {data.day} · Nº {data.index + 1} / {data.total}
              </p>
            </FadeUp>
          )}
        </div>

        {!data ? (
          <div className="h-64 flex items-center text-[#8EA696] text-sm tracking-widest uppercase" data-testid="spotlight-loading">
            Gathering today's ritual…
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-stretch" data-testid="spotlight-content">
            <div className="lg:col-span-5 relative">
              <FadeUp className="h-full">
                <div className="relative h-full min-h-[380px] flex flex-col">
                  {/* offset arch echo */}
                  <div className="absolute inset-x-6 -top-3 bottom-10 border border-[#8EA696]/35 rounded-t-[999px] translate-x-4 translate-y-4 pointer-events-none" />
                  <div className="relative rounded-t-[999px] overflow-hidden flex-1 min-h-[380px]">
                    <motion.img
                      src={data.spotlight.image || img}
                      alt={`${data.spotlight.brand} — ${data.spotlight.product}`}
                      data-testid="spotlight-product-image"
                      style={{ scale }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#11261A]/70 via-transparent to-transparent" />
                    <p className="absolute bottom-5 left-0 right-0 text-center text-[10px] uppercase tracking-[0.3em] text-[#F9FAF8]/80" data-testid="spotlight-image-note">
                      {data.spotlight.image
                        ? "Courtesy the brand"
                        : "Representative imagery only — not the actual product or brand"}
                    </p>
                  </div>
                  {/* brand chip overlapping the arch base */}
                  <div className="relative z-10 -mt-6 self-center rounded-full bg-[#F9FAF8] text-[#11261A] px-6 py-2.5 text-[10px] uppercase tracking-[0.3em] shadow-lg" data-testid="spotlight-brand-chip">
                    {data.spotlight.brand}
                  </div>
                </div>
              </FadeUp>
            </div>

            <div className="lg:col-span-7 lg:pl-10 flex flex-col justify-between gap-10">
              <div>
                <FadeUp delay={0.1}>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696] mb-3">{data.spotlight.origin}</p>
                </FadeUp>
                <MaskedLine delay={0.15}>
                  <h3 className="font-display font-light text-5xl md:text-7xl tracking-tight" data-testid="spotlight-brand-name">
                    {data.spotlight.brand}
                  </h3>
                </MaskedLine>
                <FadeUp delay={0.25}>
                  <p className="mt-6 font-display italic font-light text-2xl md:text-3xl text-[#F9FAF8]/90" data-testid="spotlight-product-name">
                    {data.spotlight.product}
                  </p>
                  <p className="mt-3 text-[#F9FAF8]/70 leading-relaxed max-w-xl text-base">
                    {data.spotlight.product_blurb}
                  </p>
                </FadeUp>
                <FadeUp delay={0.35}>
                  <div className="mt-8 flex flex-wrap gap-3" data-testid="spotlight-usp-tags">
                    {data.spotlight.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full border border-[#8EA696]/40 text-[#F9FAF8]/85 text-[11px] uppercase tracking-[0.18em] px-4 py-2 transition-colors duration-300 hover:bg-[#F9FAF8] hover:text-[#11261A]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </FadeUp>
              </div>
              <FadeUp delay={0.45}>
                <div className="border-t border-[#1B3B28] pt-6">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696] mb-3">Why it belongs at Orgænics</p>
                  <p className="font-display font-light text-xl md:text-2xl leading-snug text-[#F9FAF8]/90 max-w-2xl" data-testid="spotlight-alignment">
                    “{data.spotlight.alignment}”
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        )}
        <FadeUp className="mt-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696]/70">
            A new chapter every day — return tomorrow for the next brand
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

function VideoCard({ video, index, span = "", frameClass = "aspect-[3/4]", delay = 0, onOpen }) {
  if (!video) return null;
  return (
    <FadeUp className={span} delay={delay}>
      <button
        type="button"
        onClick={() => onOpen(video)}
        data-testid={`gallery-video-${index}-open`}
        aria-label={`Watch with sound: ${video.label}`}
        className="relative overflow-hidden group block w-full text-left cursor-pointer"
      >
        <video
          src={video.src}
          poster={video.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className={`w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 pointer-events-none ${frameClass}`}
          data-testid={`gallery-video-${index}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#11261A]/50 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 pointer-events-none">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/90">{video.label}</p>
          <span className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-[9px] uppercase tracking-[0.22em] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current" aria-hidden="true">
              <path d="M6 3l14 9-14 9V3z" />
            </svg>
            Sound on
          </span>
        </div>
      </button>
    </FadeUp>
  );
}

function Space() {
  const [active, setActive] = useState(null);
  const gallery = useContent("gallery", GALLERY_FALLBACK);
  const videos = gallery.videos && gallery.videos.length ? gallery.videos : GALLERY_FALLBACK.videos;
  const gImages =
    gallery.gallery_images && gallery.gallery_images.length ? gallery.gallery_images : GALLERY;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden" data-testid="the-space-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <FadeUp>
          <Eyebrow>The space</Eyebrow>
        </FadeUp>
        <MaskedLine delay={0.1}>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-[#0A0A0A] mt-4 max-w-2xl">
            Come in, slow down — <span className="italic text-[#11261A]">let us take care of the rest.</span>
          </h2>
        </MaskedLine>
        <FadeUp delay={0.15}>
          <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#8EA696]">
            Tap any film to watch full-screen, with sound
          </p>
        </FadeUp>
        <div className="mt-12 grid md:grid-cols-12 gap-6">
          <FadeUp className="md:col-span-7" delay={0.1}>
            <div className="overflow-hidden group">
              <img
                src={gImages[0] || GALLERY[0]}
                alt="Salon Orgænics interior"
                className="w-full h-[420px] md:h-[560px] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                data-testid="gallery-image-1"
              />
            </div>
          </FadeUp>
          <div className="md:col-span-5 flex flex-col gap-6">
            <FadeUp delay={0.2}>
              <div className="overflow-hidden group">
                <img
                  src={gImages[1] || GALLERY[1]}
                  alt="Styling chair in natural light"
                  className="w-full h-[260px] md:h-[320px] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  data-testid="gallery-image-2"
                />
              </div>
            </FadeUp>
            <VideoCard
              video={videos[0]}
              index={1}
              span="flex-1"
              frameClass="min-h-[220px]"
              delay={0.3}
              onOpen={setActive}
            />
          </div>
        </div>
        <div className="mt-6 grid md:grid-cols-12 gap-6" data-testid="gallery-video-grid">
          {videos.slice(1, 4).map((v, i) => (
            <VideoCard key={v.src} video={v} index={i + 2} span="md:col-span-4" delay={0.1 + i * 0.1} onOpen={setActive} />
          ))}
          {videos.slice(4).map((v, i) => (
            <VideoCard key={v.src} video={v} index={i + 5} span="md:col-span-6" delay={0.15 + i * 0.1} onOpen={setActive} />
          ))}
          <FadeUp className="md:col-span-6" delay={0.25}>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-instagram-card"
              className="group relative flex flex-col justify-between h-full min-h-[280px] bg-[#11261A] text-[#F9FAF8] grain overflow-hidden p-10 transition-all duration-500 hover:shadow-[0_20px_50px_rgba(17,38,26,0.25)]"
            >
              <div className="relative z-10">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696]">More films &amp; fresh work</p>
                <p className="font-display italic font-light text-3xl md:text-4xl mt-4 leading-snug">
                  Every day on<br />@salonorgaenics
                </p>
              </div>
              <span className="relative z-10 mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-[#F9FAF8]/90">
                Follow on Instagram
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </span>
            </a>
          </FadeUp>
        </div>
        <FadeUp delay={0.2} className="mt-14">
          <div className="flex flex-wrap items-center justify-between gap-6 border-t border-[#E5E7E3] pt-10">
            <p className="text-sm text-[#4A4A4A] max-w-md leading-relaxed">
              VJ Yashwin Orrizonte, near Panchshil Towers, Kharadi 411014 · Open daily 11 AM – 10 PM
            </p>
            <Link
              to="/contact"
              data-testid="space-plan-visit-link"
              className="group inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.25em] text-[#11261A]"
            >
              Plan your visit
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </FadeUp>
      </div>

      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setActive(null)}
          data-testid="video-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            data-testid="video-lightbox-close"
            aria-label="Close video"
            className="absolute top-6 right-6 md:top-10 md:right-10 text-white/80 hover:text-white transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              key={active.src}
              src={active.src}
              poster={active.poster}
              controls
              autoPlay
              playsInline
              className="max-h-[80vh] max-w-full w-auto border border-white/10"
              data-testid="video-lightbox-player"
            />
            <p className="mt-4 text-center text-[10px] uppercase tracking-[0.35em] text-white/60">
              {active.label} · Salon Orgænics
            </p>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}

function StoryStrip() {
  const gallery = useContent("gallery", GALLERY_FALLBACK);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} className="relative py-20 md:py-28 bg-[#F9FAF8] overflow-hidden" data-testid="story-strip-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <FadeUp>
            <div className="relative">
              {/* offset blob outline */}
              <div
                className="absolute -inset-2 border border-[#8EA696]/50 -translate-x-5 translate-y-5 pointer-events-none"
                style={{ borderRadius: "58% 42% 55% 45% / 48% 55% 45% 52%" }}
              />
              <motion.div style={{ y }} className="relative">
                <div style={{ borderRadius: "58% 42% 55% 45% / 48% 55% 45% 52%" }} className="overflow-hidden">
                  <img
                    src={gallery.story_image || STORY_IMG}
                    alt="Healthy natural hair in sunlight"
                    className="w-full h-[60vh] object-cover transition-transform duration-[2s] ease-out hover:scale-[1.05]"
                    data-testid="story-image"
                  />
                </div>
              </motion.div>
              {/* vine draped over the blob corner */}
              <Vine className="absolute -right-8 -top-10 h-40 w-auto opacity-60 rotate-[24deg] pointer-events-none" />
              <FloatLeaf className="w-20 -left-6 -bottom-6 opacity-40" delay={0.6} />
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
          <FadeUp>
            <Eyebrow>Our promise</Eyebrow>
          </FadeUp>
          <MaskedLine delay={0.1}>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-[#0A0A0A] mt-4">
              Beauty should be <span className="italic text-[#11261A]">thoughtful.</span>
            </h2>
          </MaskedLine>
          <FadeUp delay={0.25}>
            <p className="mt-8 text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-xl">
              Every product we choose, every service we provide and every recommendation we make is
              guided by one belief — haircare should be conscious, and great results should never
              come at the cost of what matters.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Natural & organic", "Non-toxic", "Vegan", "Cruelty-free", "Low impact"].map((v) => (
                <span key={v} className="rounded-full border border-[#1B3B28]/25 text-[#1B3B28] text-[11px] uppercase tracking-[0.18em] px-4 py-2">
                  {v}
                </span>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

const GOOGLE_URL = "https://share.google/loQbX71kZILHwidN1";
const INSTAGRAM_URL = "https://www.instagram.com/salonorgaenics";
const STAR_PATH =
  "M12 2l2.9 6.2 6.6.8-4.9 4.6 1.3 6.6L12 17l-5.9 3.2 1.3-6.6L2.5 9l6.6-.8L12 2z";

function Reviews() {
  return (
    <section className="bg-white py-20 md:py-28 overflow-hidden" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <FadeUp>
            <Eyebrow>Google reviews</Eyebrow>
          </FadeUp>
          <MaskedLine delay={0.1}>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-[#0A0A0A] mt-4">
              Loved, <span className="italic text-[#11261A]">one guest at a time.</span>
            </h2>
          </MaskedLine>
          <FadeUp delay={0.25}>
            <div className="flex gap-1.5 mt-8" data-testid="reviews-stars">
              {[0, 1, 2, 3, 4].map((i) => (
                <svg key={i} viewBox="0 0 24 24" className="w-6 h-6 fill-[#11261A]" aria-hidden="true">
                  <path d={STAR_PATH} />
                </svg>
              ))}
            </div>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-xl">
              Our guests share their experiences on our Google Business Profile — real visits,
              honest words. Your review helps neighbours across Kharadi discover conscious haircare.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={GOOGLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-read-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#11261A] text-[#F9FAF8] text-[12px] uppercase tracking-[0.22em] px-8 py-4 transition-all duration-300 hover:bg-black hover:shadow-xl hover:-translate-y-0.5"
              >
                Read our reviews
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={GOOGLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-write-button"
                className="inline-flex items-center rounded-full border border-[#11261A]/30 text-[#11261A] text-[12px] uppercase tracking-[0.22em] px-8 py-4 transition-all duration-300 hover:border-[#11261A] hover:bg-[#F9FAF8]"
              >
                Write a review
              </a>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-5">
          <FadeUp delay={0.2}>
            <div className="relative border border-[#E5E7E3] bg-[#F9FAF8] p-10 md:p-14">
              <div className="absolute -inset-2 border border-[#8EA696]/40 translate-x-3 translate-y-3 pointer-events-none" />
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#8EA696] mb-6">Find us on Google</p>
              <p className="font-display italic font-light text-2xl md:text-3xl leading-snug text-[#0A0A0A]">
                “Salon Orgænics — VJ Yashwin Orrizonte, Kharadi”
              </p>
              <p className="mt-6 text-sm text-[#4A4A4A] leading-relaxed">
                Directions, photos, hours and guest reviews — all on our Google Business Profile.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function InstagramStrip() {
  return (
    <section className="relative bg-[#F9FAF8] border-t border-[#E5E7E3] py-14 md:py-20 overflow-hidden" data-testid="instagram-section">
      <Vine className="absolute left-4 md:left-16 -top-6 h-[130%] w-auto opacity-25" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-10 relative z-10">
        <div>
          <FadeUp>
            <Eyebrow>Daily rituals, fresh work</Eyebrow>
          </FadeUp>
          <MaskedLine delay={0.1}>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight text-[#0A0A0A] mt-3">
              <span className="italic text-[#11261A]">@salonorgaenics</span>
            </h2>
          </MaskedLine>
          <FadeUp delay={0.2}>
            <p className="mt-4 text-base text-[#4A4A4A] max-w-md leading-relaxed">
              Behind the chair, new rituals and everyday transformations — follow along.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.25}>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="group inline-flex items-center gap-3.5 rounded-full bg-[#11261A] text-[#F9FAF8] text-[12px] uppercase tracking-[0.22em] px-8 py-4 transition-all duration-300 hover:bg-black hover:shadow-xl hover:-translate-y-0.5"
          >
            <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
              <circle cx="12" cy="12" r="4.2" />
              <circle cx="17.3" cy="6.7" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            Follow on Instagram
          </a>
        </FadeUp>
      </div>
    </section>
  );
}

function IntroOpener() {
  const [show, setShow] = useState(() => {
    try {
      return !sessionStorage.getItem("so_intro_seen");
    } catch {
      return false;
    }
  });

  const close = () => {
    try {
      sessionStorage.setItem("so_intro_seen", "1");
    } catch {}
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;
    document.body.style.overflow = "hidden";
    const t = setTimeout(close, 8000);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.9, ease: "easeInOut" } }}
          className="fixed inset-0 z-[100] bg-black cursor-pointer"
          onClick={close}
          data-testid="intro-opener"
          role="button"
          aria-label="Skip intro"
        >
          <motion.video
            src="/videos/salon-6.mp4"
            poster="/videos/salon-6.jpg"
            autoPlay
            muted
            playsInline
            onEnded={close}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="intro-video"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="absolute bottom-10 left-0 right-0 text-center text-[10px] uppercase tracking-[0.4em] text-white/50 pointer-events-none"
          >
            Tap anywhere to enter
          </motion.p>

        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Home() {
  return (
    <>
      <IntroOpener />
      <Hero />
      <Marquee />
      <Manifesto />
      <PhotoMarquee />
      <Spotlight />
      <Space />
      <Reviews />
      <StoryStrip />
      <PhotoMarquee />
      <InstagramStrip />
    </>
  );
}
